import { z } from "zod";

/** One row from pre-join invitations or post-join form list (field names aligned with legacy invitations). */
export const onboardInvitationRowSchema = z
  .object({
    name: z.string().optional(),
    email: z.string().optional(),
    createdOn: z.string().optional(),
    status: z.string().optional(),
    onboardingStage: z.string().nullish(),
  })
  .passthrough();

export type OnboardInvitationRow = z.infer<typeof onboardInvitationRowSchema>;

export type OnboardInvitationTableRow = OnboardInvitationRow & {
  id: string;
} & Record<string, unknown>;

const pagedBlockSchema = z
  .object({
    data: z.array(onboardInvitationRowSchema),
    totalPages: z.number().optional(),
    totalElements: z.number().optional(),
  })
  .passthrough();

/** Handles `{ data: { data, totalPages, totalElements } }` (legacy invitations shape). */
export function parseOnboardingPagedBody(raw: unknown): {
  rows: OnboardInvitationRow[];
  totalPages: number;
  totalElements: number;
} {
  const empty = { rows: [] as OnboardInvitationRow[], totalPages: 1, totalElements: 0 };
  if (raw == null || typeof raw !== "object") return empty;

  const direct = pagedBlockSchema.safeParse(raw);
  if (direct.success) {
    return {
      rows: direct.data.data,
      totalPages: direct.data.totalPages ?? 1,
      totalElements: direct.data.totalElements ?? direct.data.data.length,
    };
  }

  const root = raw as Record<string, unknown>;
  const inner = root.data;
  if (inner != null && typeof inner === "object") {
    const parsed = pagedBlockSchema.safeParse(inner);
    if (parsed.success) {
      return {
        rows: parsed.data.data,
        totalPages: parsed.data.totalPages ?? 1,
        totalElements: parsed.data.totalElements ?? parsed.data.data.length,
      };
    }
    const asArray = z.array(onboardInvitationRowSchema).safeParse(inner);
    if (asArray.success) {
      return {
        rows: asArray.data,
        totalPages: 1,
        totalElements: asArray.data.length,
      };
    }
  }
  return empty;
}

/** One row from `GET /phase2/get/form` (post-join list; not paginated). */
export const postJoinFormApiRowSchema = z
  .object({
    employeeName: z.string().optional(),
    email: z.string().optional(),
    createdOn: z.string().optional(),
    status: z.string().optional(),
    phase2Id: z.number().optional(),
    employeeId: z.number().optional(),
  })
  .passthrough();

/**
 * Parses a bare JSON array or `{ data: [...] }` from `/phase2/get/form` into
 * the same table shape as pre-join (`name`, `email`, `createdOn`, `status`).
 */
export function parsePostJoinFormListResponse(raw: unknown): OnboardInvitationRow[] {
  let items: unknown[] = [];
  if (Array.isArray(raw)) {
    items = raw;
  } else if (raw != null && typeof raw === "object") {
    const d = (raw as Record<string, unknown>).data;
    if (Array.isArray(d)) items = d;
  }

  const out: OnboardInvitationRow[] = [];
  for (const item of items) {
    const parsed = postJoinFormApiRowSchema.safeParse(item);
    if (!parsed.success) continue;
    const r = parsed.data;
    out.push({
      ...r,
      name: r.employeeName ?? "",
      email: r.email ?? "",
      createdOn: r.createdOn ?? "",
      status: r.status ?? "",
    });
  }
  return out;
}
